import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    ChevronDown, ChevronRight, CheckCircle2, ExternalLink, Image as ImageIcon,
    AlertCircle, Plus, Loader2, Pencil, Send, XCircle,
} from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
    getBroadcastUtilityTemplates,
    submitBroadcastTemplateFree,
    type BroadcastUtilityTemplate,
} from '@/services/api/broadcast';
import { getCustomTemplate } from '@/services/api/template-library';
import type { CustomTemplate } from '@/types/template-library';
import CustomTemplateFormDialog from '@/pages/Meta/TemplateLibrary/components/CustomTemplateFormDialog';

interface Props {
    pageIds: number[];
    selectedTemplateId: number | null;
    onSelect: (template: BroadcastUtilityTemplate | null) => void;
    pageFbIds: string[];
    /** Chamado quando user clica "Usar este template" após aprovação — parent
     *  seleciona o template E avança pro próximo step. */
    onUseAndAdvance?: (template: BroadcastUtilityTemplate) => void;
}

const displayTitle = (t: BroadcastUtilityTemplate): string =>
    (t.title || t.name || t.text_content || t.subtitle || 'Template sem título').trim();

interface SandboxState {
    phase: 'form' | 'submitting' | 'result';
    templateId?: number;
    result?: 'APPROVED' | 'PENDING' | 'REJECTED' | 'ERROR';
    detail?: string;
    resolvedTemplate?: BroadcastUtilityTemplate;
}

export const UtilityTemplatePicker = ({
    pageIds, selectedTemplateId, onSelect, pageFbIds, onUseAndAdvance,
}: Props) => {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState<number | null>(null);
    const [editingTemplate, setEditingTemplate] = useState<CustomTemplate | null>(null);
    const [sandbox, setSandbox] = useState<SandboxState | null>(null);
    const queryClient = useQueryClient();
    const { toast } = useToast();

    const { data, isLoading } = useQuery({
        queryKey: ['broadcast-utility-templates', [...pageIds].sort().join(',')],
        queryFn: () => getBroadcastUtilityTemplates(pageIds),
        enabled: open && pageIds.length > 0,
        staleTime: 1000 * 30,
    });

    const invalidatePicker = () =>
        queryClient.invalidateQueries({ queryKey: ['broadcast-utility-templates'] });

    // Após form salvar: submete pra 1 page aleatória e mostra resultado no wizard.
    const runSandbox = async (templateId: number) => {
        if (!pageFbIds.length) {
            setSandbox({ phase: 'result', templateId, result: 'ERROR', detail: 'Nenhuma page selecionada.' });
            return;
        }
        const testPage = pageFbIds[Math.floor(Math.random() * pageFbIds.length)];
        setSandbox({ phase: 'submitting', templateId });
        try {
            const resp = await submitBroadcastTemplateFree(templateId, [testPage]);
            const r = resp.results[0];
            invalidatePicker();
            // Se aprovou, busca o objeto completo pra "Usar"
            let resolved: BroadcastUtilityTemplate | undefined;
            if (r?.status === 'APPROVED') {
                const fresh = await getBroadcastUtilityTemplates(pageIds);
                resolved = fresh.templates.find((x) => x.id === templateId);
            }
            setSandbox({
                phase: 'result',
                templateId,
                result: r?.status ?? 'ERROR',
                detail: r?.detail || r?.reason,
                resolvedTemplate: resolved,
            });
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : String(e);
            setSandbox({ phase: 'result', templateId, result: 'ERROR', detail: msg });
        }
    };

    // Bulk: submete pra todas as pages selecionadas que NÃO têm approval APPROVED
    const submitMissing = async (t: BroadcastUtilityTemplate) => {
        const approvedSet = new Set(t.approved_pages.map((p) => p.page_id));
        const missing = pageFbIds.filter((id) => !approvedSet.has(id));
        if (!missing.length) {
            toast({ title: 'Nada a fazer', description: 'Todas as pages selecionadas já têm approval.' });
            return;
        }
        setSubmitting(t.id);
        try {
            const resp = await submitBroadcastTemplateFree(t.id, missing);
            const approved = resp.results.filter((r) => r.status === 'APPROVED').length;
            const pending = resp.results.filter((r) => r.status === 'PENDING').length;
            const errored = resp.results.filter((r) => r.status !== 'APPROVED' && r.status !== 'PENDING').length;
            toast({
                title: `Bulk submit — ${missing.length} pages`,
                description: `✓ ${approved} aprovadas · ⏳ ${pending} pendentes · ✗ ${errored} erro/rejeit.`,
            });
            invalidatePicker();
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : String(e);
            toast({ variant: 'destructive', title: 'Falha submit', description: msg });
        } finally {
            setSubmitting(null);
        }
    };

    const openEdit = async (templateId: number) => {
        try {
            const t = await getCustomTemplate(templateId);
            setEditingTemplate(t);
        } catch (e: unknown) {
            toast({ variant: 'destructive', title: 'Erro ao carregar template', description: e instanceof Error ? e.message : String(e) });
        }
    };

    const handleUseTemplate = (t: BroadcastUtilityTemplate) => {
        setSandbox(null);
        if (onUseAndAdvance) onUseAndAdvance(t);
        else onSelect(t);
    };

    if (pageIds.length === 0) return null;

    return (
        <div className="rounded-lg border bg-violet-50/30 text-sm">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-violet-100/40 rounded-lg transition"
            >
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="font-medium text-violet-800">Usar template UTILITY aprovado (opcional)</span>
                {selectedTemplateId !== null && (
                    <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-300">selecionado</Badge>
                )}
                <span className="ml-auto text-xs text-gray-500">
                    fura janela 24h · não exige message_tag
                </span>
            </button>

            {open && (
                <div className="px-3 pb-3 pt-1 space-y-2">
                    <div className="flex items-center gap-2 justify-between">
                        <div className="text-xs text-gray-600">
                            {data?.templates.length ?? 0} templates aprovados pras {pageIds.length} pages
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setSandbox({ phase: 'form' }); }}
                            className="h-7 text-xs"
                        >
                            <Plus className="w-3.5 h-3.5 mr-1" />
                            Criar novo template
                        </Button>
                    </div>

                    {isLoading && <div className="text-gray-500 py-2">Carregando templates aprovados…</div>}

                    {data && data.templates.length === 0 && !isLoading && (
                        <div className="flex items-start gap-2 text-gray-600 py-2 px-2 bg-amber-50 rounded">
                            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                            <div>
                                Nenhum template UTILITY aprovado pras pages selecionadas. Clique em
                                "Criar novo template" pra submeter um pra aprovação.
                            </div>
                        </div>
                    )}

                    {selectedTemplateId !== null && (
                        <button
                            type="button"
                            onClick={() => onSelect(null)}
                            className="text-xs text-violet-700 hover:underline"
                        >
                            ↶ Limpar seleção (voltar pra envio Standard)
                        </button>
                    )}

                    {data?.templates.map((t) => {
                        const isSel = t.id === selectedTemplateId;
                        const partial = t.coverage_approved < t.coverage_total;
                        const busy = submitting === t.id;
                        return (
                            <div
                                key={t.id}
                                className={`rounded border p-2.5 transition ${
                                    isSel
                                        ? 'bg-emerald-50 border-emerald-400 ring-1 ring-emerald-300'
                                        : 'bg-white border-gray-200 hover:border-violet-400'
                                }`}
                            >
                                <button
                                    type="button"
                                    onClick={() => onSelect(isSel ? null : t)}
                                    className="w-full text-left"
                                >
                                    <div className="flex items-start gap-2.5">
                                        {isSel && <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />}
                                        {!isSel && <div className="w-4" />}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-gray-900 truncate">{displayTitle(t)}</span>
                                                {t.image_url && <ImageIcon className="w-3.5 h-3.5 text-gray-400" />}
                                            </div>
                                            {t.subtitle && t.subtitle !== displayTitle(t) && (
                                                <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">{t.subtitle}</div>
                                            )}
                                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                                <Badge
                                                    className={
                                                        partial
                                                            ? 'bg-amber-100 text-amber-700 border-amber-300'
                                                            : 'bg-emerald-100 text-emerald-700 border-emerald-300'
                                                    }
                                                >
                                                    ✓ {t.coverage_approved}/{t.coverage_total} pages aprovadas
                                                </Badge>
                                                {t.approved_url_examples[0] && (
                                                    <a
                                                        href={t.approved_url_examples[0]}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="text-xs text-violet-700 hover:underline flex items-center gap-1 truncate max-w-[260px]"
                                                        title={t.approved_url_examples[0]}
                                                    >
                                                        <ExternalLink className="w-3 h-3" />
                                                        {t.approved_url_examples[0]}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </button>
                                <div className="flex items-center gap-1.5 mt-2 pl-6">
                                    {partial && (
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            className="h-6 text-[11px] px-2"
                                            disabled={busy}
                                            onClick={(e) => { e.stopPropagation(); submitMissing(t); }}
                                        >
                                            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                                            Submeter pras {t.coverage_total - t.coverage_approved} faltantes
                                        </Button>
                                    )}
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="h-6 text-[11px] px-2"
                                        disabled={busy}
                                        onClick={(e) => { e.stopPropagation(); openEdit(t.id); }}
                                    >
                                        <Pencil className="w-3 h-3 mr-1" />
                                        Editar e reenviar
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Wizard: form → submitting → result. Mesmo dialog aberto durante todas as fases. */}
            <Dialog
                open={sandbox !== null}
                onOpenChange={(o) => { if (!o) setSandbox(null); }}
            >
                <DialogContent className="sm:max-w-md">
                    {sandbox?.phase === 'form' && (
                        <>
                            {/* Delega o form ao CustomTemplateFormDialog, mantendo consistência com Template Library.
                                Ele fecha sozinho após salvar; o onSaved dispara o próximo passo do wizard. */}
                            <CustomTemplateFormDialog
                                open={true}
                                onOpenChange={(o) => { if (!o) setSandbox(null); }}
                                mode="create"
                                onSaved={(newId?: number) => {
                                    if (newId) runSandbox(newId);
                                    else setSandbox(null);
                                }}
                            />
                        </>
                    )}
                    {sandbox?.phase === 'submitting' && (
                        <>
                            <DialogHeader>
                                <DialogTitle>Enviando pra Meta…</DialogTitle>
                            </DialogHeader>
                            <div className="flex flex-col items-center justify-center py-8 gap-3">
                                <Loader2 className="w-10 h-10 text-violet-600 animate-spin" />
                                <p className="text-sm text-gray-600 text-center">
                                    Testando o template em 1 page pra ver se a Meta aprova.
                                    <br />
                                    <span className="text-xs text-gray-500">Isso leva alguns segundos.</span>
                                </p>
                            </div>
                        </>
                    )}
                    {sandbox?.phase === 'result' && (
                        <>
                            <DialogHeader>
                                <DialogTitle>
                                    {sandbox.result === 'APPROVED' && '✓ Aprovado pela Meta'}
                                    {sandbox.result === 'PENDING' && '⏳ Em análise pela Meta'}
                                    {(sandbox.result === 'REJECTED' || sandbox.result === 'ERROR') && '✗ Não aprovado'}
                                </DialogTitle>
                            </DialogHeader>
                            <div className="py-4">
                                {sandbox.result === 'APPROVED' && (
                                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex flex-col items-center gap-3">
                                        <CheckCircle2 className="w-14 h-14 text-emerald-600" />
                                        <div className="text-center">
                                            <div className="font-semibold text-emerald-900">Template aprovado ✓</div>
                                            <p className="text-xs text-emerald-800 mt-1">
                                                Testado em 1 page. Clique em <b>Usar este template</b> pra selecionar e continuar o broadcast.
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {sandbox.result === 'PENDING' && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex flex-col items-center gap-3">
                                        <Loader2 className="w-12 h-12 text-amber-600" />
                                        <div className="text-center">
                                            <div className="font-semibold text-amber-900">Meta está avaliando</div>
                                            <p className="text-xs text-amber-800 mt-1">
                                                A aprovação pode levar de alguns segundos a alguns minutos. Feche e volte em ~1min pra ver o status.
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {(sandbox.result === 'REJECTED' || sandbox.result === 'ERROR') && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex flex-col items-start gap-2">
                                        <div className="flex items-center gap-2 text-red-900 font-semibold">
                                            <XCircle className="w-5 h-5" /> {sandbox.result === 'REJECTED' ? 'Rejeitado' : 'Erro'}
                                        </div>
                                        {sandbox.detail && (
                                            <div className="text-xs text-red-800 font-mono bg-red-100 p-2 rounded w-full break-words">
                                                {sandbox.detail}
                                            </div>
                                        )}
                                        <p className="text-xs text-red-700 mt-1">
                                            Clique em <b>Editar</b> pra ajustar e tentar de novo.
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center justify-end gap-2">
                                <Button type="button" variant="outline" size="sm" onClick={() => setSandbox(null)}>
                                    Fechar
                                </Button>
                                {(sandbox.result === 'REJECTED' || sandbox.result === 'ERROR') && sandbox.templateId && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        onClick={() => {
                                            const id = sandbox.templateId!;
                                            setSandbox(null);
                                            openEdit(id);
                                        }}
                                    >
                                        <Pencil className="w-3.5 h-3.5 mr-1" /> Editar template
                                    </Button>
                                )}
                                {sandbox.result === 'APPROVED' && sandbox.resolvedTemplate && (
                                    <Button
                                        type="button"
                                        size="sm"
                                        className="bg-emerald-600 hover:bg-emerald-700"
                                        onClick={() => handleUseTemplate(sandbox.resolvedTemplate!)}
                                    >
                                        Usar este template →
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <CustomTemplateFormDialog
                open={editingTemplate !== null}
                onOpenChange={(o) => { if (!o) setEditingTemplate(null); }}
                mode="edit"
                template={editingTemplate}
                onSaved={() => {
                    if (editingTemplate) runSandbox(editingTemplate.id);
                    setEditingTemplate(null);
                }}
            />
        </div>
    );
};
